export type DynamicInput = [key: string, description: string];

export interface DynamicInputsOptions {
    reminderFeatureFlag?: boolean;
}

// Canonical source of which $placeholder tokens are available for a given alert
// trigger field. Shared by the dynamic-input pickers in Alert Templates, Rules,
// and the incompatible-placeholder checker in AlertTemplateHelper.
export function getAvailableDynamicInputs(field: string | null | undefined, options: DynamicInputsOptions = {}): DynamicInput[] {
    if (!field) {
        return [];
    }

    const { reminderFeatureFlag = false } = options;

    const triggerStatus: DynamicInput = ['triggerStatus', `Status for the trigger: Triggering${reminderFeatureFlag ? ', Reminder' : ''} or Resolved`];
    const name: DynamicInput = ['name', 'Device name (device ID if no name is set)'];
    const description: DynamicInput = ['description', 'Device description'];
    const celsius: DynamicInput = ['celsius', 'Temperature sensor value in °C'];
    const fahrenheit: DynamicInput = ['fahrenheit', 'Temperature sensor value in °F'];
    // On a virtualGlycolBuffer trigger, $celsius/$fahrenheit resolve to the glycol temperature
    const glycolCelsius: DynamicInput = ['celsius', 'Virtual glycol buffer temperature in °C'];
    const glycolFahrenheit: DynamicInput = ['fahrenheit', 'Virtual glycol buffer temperature in °F'];
    const co2: DynamicInput = ['co2', 'CO2 sensor value in PPM'];
    const pascal: DynamicInput = ['pascal', 'Differential pressure sensor value in Pa'];
    const inH2O: DynamicInput = ['inH2O', 'Differential pressure sensor value in inH2O'];
    const motion: DynamicInput = ['motion', 'Motion sensor status as "motion detected" or "no motion detected"'];
    const deskOccupancy: DynamicInput = ['deskOccupancy', 'Desk occupancy state as "occupied" or "not occupied"'];
    const contact: DynamicInput = ['openOrClosed', 'Door/Window status as "open" or "closed"'];
    const proximityStatus: DynamicInput = ['proximityStatus', 'Proximity sensor status as "open" or "closed"'];
    const objectPresent: DynamicInput = ['objectPresent', 'Proximity sensor status as "present" or "not present"'];
    const relativeHumidity: DynamicInput = ['relativeHumidity', 'Relative humidity value as %RH'];
    const waterPresent: DynamicInput = ['waterPresent', 'Water sensor status as "water present" or "no water present"'];
    const batteryState: DynamicInput = ['batteryState', 'Battery state as "ok", "low" or "critical"'];
    const deviceID: DynamicInput = ['deviceID', 'Device ID'];
    const label: DynamicInput = ['label.labelKey', 'Access label values by providing the labelKey'];
    const triggerTime: DynamicInput = ['triggerTime', 'Timestamp in the project\'s timezone when the device met the trigger condition. You can use triggerTime:en-US or triggerTime:nb-NO for regional formatting.'];
    const timeSinceTrigger: DynamicInput = ['timeSinceTrigger', 'Time since the device met the trigger condition. Eg. "1 hour 30 minutes" or "less than a minute".'];
    const projectDisplayName: DynamicInput = ['projectDisplayName', 'Project display name'];
    const projectID: DynamicInput = ['projectID', 'Project ID'];

    switch (field) {
        case 'touch':
        case 'touchCount':
        case 'proximityCount':
        case 'contactCount':
            return [name, description, deviceID, label, timeSinceTrigger, triggerTime, projectDisplayName, projectID];
        case 'temperature':
            return [triggerStatus, name, description, celsius, fahrenheit, deviceID, label, timeSinceTrigger, triggerTime, projectDisplayName, projectID];
        case 'virtualGlycolBuffer':
            return [triggerStatus, name, description, glycolCelsius, glycolFahrenheit, deviceID, label, timeSinceTrigger, triggerTime, projectDisplayName, projectID];
        case 'relativeHumidity':
            return [triggerStatus, name, description, relativeHumidity, celsius, fahrenheit, deviceID, label, timeSinceTrigger, triggerTime, projectDisplayName, projectID];
        case 'co2':
            return [triggerStatus, name, description, co2, deviceID, label, timeSinceTrigger, triggerTime, projectDisplayName, projectID];
        case 'differentialPressure':
            return [triggerStatus, name, description, pascal, inH2O, deviceID, label, timeSinceTrigger, triggerTime, projectDisplayName, projectID];
        case 'motion':
            return [triggerStatus, name, description, motion, deviceID, label, timeSinceTrigger, triggerTime, projectDisplayName, projectID];
        case 'deskOccupancy':
            return [triggerStatus, name, description, deskOccupancy, deviceID, label, timeSinceTrigger, triggerTime, projectDisplayName, projectID];
        case 'contact':
            return [triggerStatus, name, description, contact, deviceID, label, timeSinceTrigger, triggerTime, projectDisplayName, projectID];
        case 'objectPresent':
            return [triggerStatus, name, description, proximityStatus, objectPresent, deviceID, label, timeSinceTrigger, triggerTime, projectDisplayName, projectID];
        case 'waterPresent':
            return [triggerStatus, name, description, waterPresent, deviceID, label, timeSinceTrigger, triggerTime, projectDisplayName, projectID];
        case 'connectionStatus':
            return [triggerStatus, name, description, deviceID, label, timeSinceTrigger, triggerTime, projectDisplayName, projectID];
        case 'batteryStatus':
            return [triggerStatus, name, description, batteryState, deviceID, label, timeSinceTrigger, triggerTime, projectDisplayName, projectID];
        default:
            return [];
    }
}
